import React, {FC, PropsWithChildren, useCallback, useEffect} from 'react';
// import * as Facebook from "expo-auth-session/providers/facebook";
// import {ResponseType} from "expo-auth-session";
// import * as WebBrowser from "expo-web-browser";
import {observer} from 'mobx-react-lite';
import {Button, I18NText, Text} from '@yosmanyga/rich-ui';
import type {UnnormalizedProps} from '@yosmanyga/core-style';
import {
    AuthenticationFrontendAsGuestController,
    AuthenticationFrontendWithFacebookController
} from '@yosmanyga/authentication-frontend';
import {useService} from '@yosmanyga/di';
import {ThemeProvider, ThemeVariablesType} from '@yosmanyga/theme';
import {ManuscriptLayoutProps} from "../../layout";
import {AppLanguageController} from "../../controller";
import merge from 'lodash/merge';

const Welcome = observer(({
    enabled,
    templates,
    onLogin,
    onIntroduction
}: WelcomeProps) => {
    const authenticationFrontendAsGuestController = useService<AuthenticationFrontendAsGuestController>(AuthenticationFrontendAsGuestController, []);
    const authenticationFrontendWithFacebookController = useService<AuthenticationFrontendWithFacebookController>(AuthenticationFrontendWithFacebookController, []);
    const appLanguageController = useService(AppLanguageController, []);

    useEffect(() => {
        (async () => {
            await appLanguageController.load();
        })();
    }, []);

    const OuterLayout = useCallback(templates.outerLayout, []);
    const LanguagesLayout = useCallback(templates.languagesLayout, []);
    const HeaderLayout = useCallback(templates.headerLayout, []);
    const ManuscriptLayout = useCallback(templates.manuscriptLayout, []);

    if (!appLanguageController.loaded) {
        return null;
    }

    return <OuterLayout>
        <LanguagesLayout>
            <Button
                intent="secondary"
                onClick={async () => {
                    await appLanguageController.set('en');
                }}
            >
                <Text>English</Text>
            </Button>

            <Button
                intent="secondary"
                margin={{
                    left: 1,
                }}
                onClick={async () => {
                    await appLanguageController.set('es');
                }}
            >
                <Text>Español</Text>
            </Button>
        </LanguagesLayout>

        <HeaderLayout>
            <I18NText intent="header">The Fortaleza</I18NText>
            <I18NText
                intent="subheader"
                margin={{
                    bottom: 2,
                }}
            >
                Inside The Beast' guts
            </I18NText>
        </HeaderLayout>

        <ManuscriptLayout
            footer={true}
            flow="column"
            align={{
                main: 'center',
                cross: 'center',
            }}
            width={350}
            height='auto'
        >
            {/*<FacebookEntry*/}
            {/*  intent="primary"*/}
            {/*  progress={authenticationFrontendWithFacebookController.completing}*/}
            {/*  disabled={!enabled}*/}
            {/*  pseudo={{*/}
            {/*    hover: !enabled*/}
            {/*      ? {*/}
            {/*        background: 'inherit',*/}
            {/*      }*/}
            {/*      : undefined,*/}
            {/*  }}*/}
            {/*  onDone={async (code, verifier) => {*/}
            {/*    await authenticationFrontendWithFacebookController.complete(code, verifier);*/}

            {/*    onLogin();*/}
            {/*  }}*/}
            {/*>*/}
            {/*  <I18NText>Log in using Facebook</I18NText>*/}
            {/*</FacebookEntry>*/}

            <Button
                id='login'
                intent="primary"
                progress={authenticationFrontendAsGuestController.completing}
                onClick={enabled ? async () => {
                    await authenticationFrontendAsGuestController.complete();

                    onLogin();
                } : undefined}
                pseudo={{
                    hover: !enabled
                        ? {
                            background: 'inherit',
                        }
                        : undefined,
                }}
            >
                <I18NText>Log in as anonymous</I18NText>
            </Button>
            <Button
                intent="primary"
                onClick={() => {
                    onIntroduction();
                }}
            >
                <I18NText>Introduction</I18NText>
            </Button>
        </ManuscriptLayout>
    </OuterLayout>
});

type WelcomeProps = {
    enabled: boolean;
    templates: {
        outerLayout: FC<PropsWithChildren<UnnormalizedProps>>,
        languagesLayout: FC<PropsWithChildren<UnnormalizedProps>>,
        headerLayout: FC<PropsWithChildren<UnnormalizedProps>>,
        manuscriptLayout: FC<ManuscriptLayoutProps>,
    },
    onLogin: () => void,
    onIntroduction: () => void,
};

const WelcomeWithTheme = (props: WelcomeProps) => {
    return <ThemeProvider
        build={(variables, prev) => {
            const {palette, font} = variables as ThemeVariablesType;

            const current = {
                text: {
                    header_intent: {
                        color: palette.neutral['400'],
                        font: {
                            size: font('150%'),
                        }
                    },
                    subheader_intent: {
                        color: palette.brand1['800'],
                        font: {
                            size: font('80%'),
                        }
                    },
                },
                button: {
                    border: 0,
                    padding: {
                        top: 0.5,
                        bottom: 1,
                        left: 1,
                        right: 1,
                    },
                    color: palette.neutral['1000'],
                    primary_intent: {
                        color: palette.neutral['1000'],
                        background: 'transparent',
                        border: 0,
                        width: '100%',
                        padding: {
                            top: 2,
                            bottom: 2
                        },
                        pseudo: {
                            hover: {
                                color: palette.neutral['0'],
                                border: 0,
                            },
                        },
                    },
                    secondary_intent: {
                        padding: 1,
                        color: palette.neutral['400'],
                        pseudo: {
                            hover: {
                                color: palette.neutral['1000'],
                                background: palette.neutral['400'],
                            },
                        },
                    },
                },
            };

            return merge(
                {},
                prev ? prev(variables) : {},
                current
            );
        }}
    >
        <Welcome {...props} />
    </ThemeProvider>
};

// WebBrowser.maybeCompleteAuthSession();

// const FacebookEntry = ({
//     disabled,
//     onDone,
//     children,
//     ...props
// }: PropsWithChildren<{
//     disabled: boolean,
//     onDone: (
//         code: string,
//         verifier: string
//     ) => void
// } & UnnormalizedProps>) => {
//     const [request, response, promptAsync] = Facebook.useAuthRequest({
//         // clientId: '1255065088417741', // The Fortaleza, as Game type, doesn't work
//         clientId: '902425037550121', // The Fortaleza 2
//         responseType: ResponseType.Code,
//     });
//
//     useEffect(() => {
//         (async () => {
//             if (response?.type === 'success') {
//                 const {code} = response.params;
//
//                 if (!request || !request.codeVerifier) {
//                     throw new Error();
//                 }
//
//                 onDone(code, request.codeVerifier);
//             }
//         })()
//     }, [response]);
//
//     return <Button
//         onClick={!disabled
//             ? () => {
//                 promptAsync();
//             }
//             : undefined
//         }
//         {...props}
//     >
//         {children}
//     </Button>
// };

export {WelcomeWithTheme as Welcome};
